import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { Dialog, DialogContent, DialogTitle, DialogDescription, DialogClose } from "../ui/dialog";
import { api, fmtDate, platformStyle } from "../../lib/api";
import { VoteButtons } from "./VoteButtons";
import { AddToCalendar } from "./AddToCalendar";

const ReleaseModalContext = createContext({ openRelease: () => {} });

export const useReleaseModal = () => useContext(ReleaseModalContext);

const channels = (r, isReleased) => {
  const list = (r.platforms || []).filter(Boolean);
  if (r.type === "Film" && (!list.length || (list.length === 1 && list[0] === "À venir"))) {
    return [isReleased ? "En salle" : "Au cinéma"];
  }
  return list.length ? list : ["À venir"];
};

// English TMDB synopsis fallback: null = loading, "" = none.
const useOverviewEn = (r) => {
  const [text, setText] = useState("");
  const missing = !r.overview?.trim();
  useEffect(() => {
    if (!missing) return;
    let alive = true;
    setText(null);
    api
      .get("/releases/overview-en", { params: { type: r.type, tmdb_id: r.tmdb_id } })
      .then((res) => alive && setText(res.data.overview || ""))
      .catch(() => alive && setText(""));
    return () => {
      alive = false;
    };
  }, [r.type, r.tmdb_id, missing]);
  return missing ? text : "";
};

const ReleaseDetails = ({ r, inDb }) => {
  const overviewEn = useOverviewEn(r);
  const today = new Date().toISOString().slice(0, 10);
  const isReleased = !!r.release_date && r.release_date <= today;
  const poster = r.poster_url?.replace("/w342/", "/w500/");
  const showOriginal =
    r.original_title && r.original_title.trim().toLowerCase() !== (r.title || "").trim().toLowerCase();

  return (
    <>
    <div className="flex items-center gap-3 -mt-1 mb-5 pr-8" data-testid="release-modal-brand">
      <img src="/logo-email.png" alt="Rouge Première" className="h-9 md:h-10 w-auto select-none" draggable={false} />
    </div>
    <div className="grid sm:grid-cols-[240px_1fr] gap-6 md:gap-8" data-testid="release-modal-body">
      <div className="w-[200px] sm:w-[240px] mx-auto sm:mx-0 aspect-[2/3] bg-neutral-100 rounded overflow-hidden border border-[var(--line)]">
        {poster ? (
          <img src={poster} alt={r.title} className="w-full h-full object-cover" data-testid="release-modal-poster" />
        ) : (
          <div className="grid place-items-center h-full text-xs text-neutral-400 italic">Pas d'affiche</div>
        )}
      </div>
      <div className="flex flex-col min-w-0">
        <div className="text-[10px] uppercase tracking-[0.3em] text-[var(--red)] font-medium">{r.type}</div>
        <DialogTitle className="font-display text-2xl md:text-3xl leading-tight mt-1 pr-6" data-testid="release-modal-title">
          {r.title}
        </DialogTitle>
        {showOriginal && <div className="text-sm text-neutral-400 italic mt-0.5">{r.original_title}</div>}

        <div className="mt-4 flex flex-wrap items-center gap-2">
          {channels(r, isReleased).map((p) => (
            <span
              key={p}
              data-testid="release-modal-platform"
              className={`text-[10px] uppercase tracking-[0.12em] font-semibold px-2 py-1 rounded-sm ${platformStyle[p] || platformStyle["À venir"]}`}
            >
              {p}
            </span>
          ))}
          {r.release_date && (
            <span className="text-xs text-neutral-600" data-testid="release-modal-date">
              {isReleased ? "Sorti le" : "Sortie le"} {fmtDate(r.release_date)}
            </span>
          )}
        </div>
        {(r.genres?.length > 0 || r.countries?.length > 0) && (
          <div className="mt-2 text-[11px] uppercase tracking-[0.15em] text-neutral-500">
            {[...(r.genres || []).slice(0, 4), ...(r.countries || []).slice(0, 2)].join(" · ")}
          </div>
        )}

        <DialogDescription asChild>
          <p className="mt-5 text-sm md:text-[15px] leading-relaxed text-neutral-700 whitespace-pre-line" data-testid="release-modal-overview">
            {r.overview?.trim() || overviewEn || (overviewEn === null ? "…" : "Synopsis non disponible pour le moment.")}
          </p>
        </DialogDescription>
        {!r.overview?.trim() && overviewEn && (
          <div className="mt-1.5 text-[10px] uppercase tracking-[0.2em] text-neutral-400" data-testid="release-modal-overview-en-label">
            Synopsis en anglais · version française pas encore disponible
          </div>
        )}

        {inDb && (
          <div className="mt-6 pt-4 border-t border-[var(--line)] flex flex-wrap items-center gap-4" data-testid="release-modal-actions">
            {isReleased ? (
              <VoteButtons release={r} />
            ) : (
              r.release_date && <AddToCalendar release={r} variant="button" />
            )}
          </div>
        )}

        <div className="mt-auto pt-6 flex items-center justify-between gap-3">
          {r.manual ? (
            <div className="text-[10px] text-neutral-400" data-testid="release-modal-editorial-credit">Fiche rédigée par la rédaction</div>
          ) : (
            <div className="text-[10px] text-neutral-400 flex items-center gap-1.5" data-testid="release-modal-tmdb-credit">
              Source :
              <a href="https://www.themoviedb.org/" target="_blank" rel="noopener noreferrer" className="inline-flex items-center">
                <img src="/tmdb-logo.svg" alt="TMDB" className="h-2.5 w-auto" />
              </a>
            </div>
          )}
          <DialogClose
            data-testid="release-modal-close-btn"
            className="text-xs uppercase tracking-[0.25em] px-4 py-2 border border-[var(--line-strong)] rounded-sm hover:bg-black hover:text-white transition-colors"
          >
            Fermer
          </DialogClose>
        </div>
      </div>
    </div>
    </>
  );
};

export const ReleaseModalProvider = ({ children }) => {
  const [release, setRelease] = useState(null);
  const [inDb, setInDb] = useState(false);

  const openRelease = useCallback((r, opts = {}) => {
    setRelease(r);
    setInDb(!opts.external);
  }, []);

  useEffect(() => {
    if (!release || inDb) return;
    let alive = true;
    api
      .get("/releases/item", { params: { type: release.type, tmdb_id: release.tmdb_id } })
      .then((res) => {
        if (!alive) return;
        setRelease((cur) => ({ ...cur, ...res.data, overview: res.data.overview || cur.overview }));
        setInDb(true);
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, [release?.tmdb_id, release?.type]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <ReleaseModalContext.Provider value={{ openRelease }}>
      {children}
      <Dialog open={!!release} onOpenChange={(o) => !o && setRelease(null)}>
        <DialogContent
          data-testid="release-modal"
          onInteractOutside={(e) => {
            if (e.target.closest?.('[data-testid^="add-to-calendar-popover-"]')) e.preventDefault();
          }}
          className="max-w-3xl w-[calc(100%-1.5rem)] max-h-[90vh] overflow-y-auto bg-white rounded-md p-6 md:p-8 border-t-4 border-t-[var(--red)]"
        >
          {release && <ReleaseDetails r={release} inDb={inDb} />}
        </DialogContent>
      </Dialog>
    </ReleaseModalContext.Provider>
  );
};
